import { formatDateTime } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { Signature } from '@seen/core/models/signature'
import { SignatureButtonNode as SignatureButtonSchema } from '@seen/core/tiptap/schema'
import { parseReferenceString } from '@seen/core/utils'
import { Button, TypographyPUI } from '@seen/ui'
import { IconSignature } from '@tabler/icons-react'
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { format, parseISO } from 'date-fns'
import { type JSX, useContext, useState } from 'react'
import { toast } from 'sonner'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { PractitionerName } from '#src/components/PractitionerName.tsx'
import { noteButtonClass } from '#src/components/rich-text-editor/noteButtonClass.ts'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRead } from '#src/hooks/useRead.ts'

export const SignatureButtonNode = SignatureButtonSchema.extend({
  addNodeView() {
    return ReactNodeViewRenderer(SignatureButtonNodeComponent)
  },
})

function SignatureButtonNodeComponent(props: NodeViewProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  const signature = props.node.attrs.signature
  // Deliberately React state and NOT a node attribute. An attribute write is a
  // document change, so it is both serialized into the note body and (with
  // save-on-any-local-edit) enough on its own to queue a save. The node view is
  // frequently gone before the create resolves — `updateAttributes` is a no-op
  // once `getPos()` stops returning a number — so a persisted spinner flag would
  // have no writer left to clear it and the button would come back stuck.
  const [isSigning, setIsSigning] = useState(false)
  const { data: signatureInstance } = useRead(Signature, signature)
  const createSignature = useCreateMutation<Signature>().mutate
  const currentPractitioner = useCurrentPractitioner()
  const compositionReferenceString = useContext(CompositionContext)

  return (
    <NodeViewWrapper className="inline-flex">
      {(!signatureInstance ||
        signatureInstance.target.reference !== compositionReferenceString) && (
        <Button
          variant="unstyled"
          size="unstyled"
          className={
            inlineCapture
              ? noteButtonClass(true)
              : 'inline rounded-xs border-[0.5px] border-gray-450 bg-gray-470 px-1 py-px hover:bg-gray-460'
          }
          loading={isSigning}
          disabled={!props.editor.isEditable || isReadOnly}
          onClick={() => {
            setIsSigning(true)
            const compositionReference = parseReferenceString(
              compositionReferenceString
            )
            invariant(compositionReference, 'Composition context is required')
            const sig = new Signature({
              target: compositionReference,
              who: currentPractitioner.reference,
            })
            createSignature(sig, {
              onSuccess: (signature) => {
                setIsSigning(false)
                props.updateAttributes({
                  signature: signature.reference.reference,
                  display: `Signed by ${currentPractitioner.name} on ${formatDateTime(sig.recorded)}`,
                })
              },
              onError: (error) => {
                setIsSigning(false)
                toast.error(`Failed to sign: ${error}`)
              },
            })
          }}
        >
          <span className="inline-flex">
            <IconSignature className="relative top-0.5 left-px size-3.5 shrink-0 text-foreground-secondary" />
          </span>
          <span className="text-foreground-tertiary"> __________</span>
        </Button>
      )}
      {signatureInstance &&
        signatureInstance.target.reference === compositionReferenceString && (
          <Tooltip>
            <TooltipTrigger asChild tabIndex={-1}>
              <span className="rounded-sm border-[0.5px] border-gray-450 bg-gray-470 px-1 py-px hover:bg-gray-460">
                <span className="inline-flex">
                  <IconSignature className="relative top-0.5 left-px size-3.5 shrink-0 text-foreground-secondary" />
                </span>
                <span>
                  {' '}
                  <PractitionerName practitioner={signatureInstance.who} />
                </span>
                <span> at {formatDateTime(signatureInstance.recorded)}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="center" className="-ml-3">
              <TypographyPUI className="font-normal">
                {format(
                  parseISO(signatureInstance.recorded),
                  "EEEE, MMMM d, yyyy 'at' h:mm a"
                )}
              </TypographyPUI>
            </TooltipContent>
          </Tooltip>
        )}
    </NodeViewWrapper>
  )
}
